import classnames from 'classnames'
import React, { useCallback, useState } from 'react'
import { IProps } from './index'
import styles from './styles.scss'
import { RichText } from '../RichText'
import { CTALink } from '../CTALink'
import { ModalWithPersistedState } from 'marketing-site/src/library/elements/ModalWithPersistedState'
import { CTATheme, mq } from '../../utils'
import { useMediaQuery } from 'marketing-site/src/library/utils/mediaQuery'
import { Text } from '../Text'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'

export const CustomerStoryCard = ({
  allowFeatured,
  title,
  customerStoryUrl,
  image,
  logo,
  ctaText,
  isFeatured,
  snapshotImage,
  snapshotTitle,
  snapshotBody,
  snapshotSidebar,
  snapshotCta,
}: IProps): React.ReactElement => {
  const cardClassNames = classnames('customer-story-card', {
    'customer-story-card--featured': allowFeatured && isFeatured,
  })

  const { translate } = useTranslate()
  const forceSmallTitle = useMediaQuery(`(${mq.tablet}) and (max-width: 1200px)`)
  const [modalOpen, setModalOpen] = useState(false)

  const openModal = useCallback(() => {
    setModalOpen(true)
  }, [setModalOpen])

  const closeModal = useCallback(() => {
    setModalOpen(false)
  }, [setModalOpen])

  return (
    <div className={cardClassNames}>
      <div className="customer-story-card__image-container">
        <img className="customer-story-card__image" src={image.url} alt={image.altText} />
        <div className="customer-story-card__logo-container">
          <img className="customer-story-card__logo" src={logo.logoUrl} alt={logo.companyName} />
        </div>
      </div>
      <div className="customer-story-card__content">
        <div className="customer-story-card__title">
          <Text
            brand="default"
            size={!forceSmallTitle && allowFeatured && isFeatured ? 'heading-4' : 'body'}
          >
            <RichText html={title} />
          </Text>
        </div>
        <button className="customer-story-card__snapshot-cta" onClick={openModal}>
          {translate('See a snapshot')}
        </button>
        <div className="customer-story-card__cta">
          <CTALink text={ctaText} url={customerStoryUrl} bgColor={CTATheme.LinkOnlyBlack} />
        </div>
        <ModalWithPersistedState
          isOpen={modalOpen}
          onRequestClose={closeModal}
          size="71.5875rem"
          borderRadius="24px"
          showCloseIcon={false}
        >
          <div className="customer-story-card__modal">
            <button
              className="customer-story-card__modal__close-button"
              type="button"
              onClick={closeModal}
            >
              <svg
                width="32"
                height="28"
                viewBox="0 0 32 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 2.00003L28 26"
                  stroke="#081D34"
                  strokeWidth="2.47"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 26L28 1.99997"
                  stroke="#081D34"
                  strokeWidth="2.47"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="customer-story-card__modal__sidebar">
              <div className="customer-story-card__modal__logo-container">
                <img
                  className="customer-story-card__modal__logo"
                  src={snapshotImage.logoUrl}
                  alt={snapshotImage.companyName}
                />
              </div>
              <div className="customer-story-card__modal__sidebar__body">
                {snapshotSidebar && <RichText html={snapshotSidebar} />}
              </div>
            </div>
            <div className="customer-story-card__modal__main">
              <div className="customer-story-card__modal__title">
                <Text brand="default" size="heading-4">
                  {snapshotTitle}
                </Text>
              </div>
              {snapshotBody && (
                <div className="customer-story-card__modal__body">
                  <RichText html={snapshotBody} />
                </div>
              )}
              <div className="customer-story-card__modal__cta">
                <CTALink text={snapshotCta} url={customerStoryUrl} bgColor={CTATheme.BlackFill} />
              </div>
            </div>
          </div>
        </ModalWithPersistedState>
      </div>
      <style jsx>{styles}</style>
    </div>
  )
}
